import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { isVeevaMedcomms } from 'const';
import { TextAbbreviationDocument, TextAbbreviationDocumentsArray } from 'models';
import { selectAbbreviationsSortedData } from '../store/selectors';

export default function useFilteredAbbreviations(termToSearch: string | undefined): {
  abbreviationsList: TextAbbreviationDocumentsArray;
  caseCorrectedTerm: string;
} {
  const textAbbreviationDocuments: TextAbbreviationDocumentsArray = useSelector(selectAbbreviationsSortedData);

  return useMemo(() => {
    if (!termToSearch) {
      return { abbreviationsList: [], caseCorrectedTerm: '' };
    }

    const abbreviationsListFiltered = textAbbreviationDocuments
      .filter(
        ({ abbreviation }) => abbreviation?.term?.trim().toLowerCase() === termToSearch?.trim().toLowerCase(),
      );

    // Remove duplicates, keep the one with the lowest id (the first created)
    const abbreviationsMap = new Map<string, TextAbbreviationDocument>();
    if (isVeevaMedcomms()) {
      abbreviationsListFiltered.forEach((doc) => {
        const key = doc.abbreviation?.definition?.trim().toLowerCase();

        const existingDoc = abbreviationsMap.get(key);
        if (!existingDoc || existingDoc.id > doc.id) {
          abbreviationsMap.set(key, doc);
        }
      });
    }

    const abbreviationsList = isVeevaMedcomms() ? Array.from(abbreviationsMap.values()) : abbreviationsListFiltered;
    const [firstTextAbbreviationDocument] = abbreviationsList;
    // When first abbreviation is created user is not able to create variants of term anymore
    const caseCorrectedTerm = firstTextAbbreviationDocument?.abbreviation?.term ?? termToSearch;

    return {
      abbreviationsList,
      caseCorrectedTerm,
    };
  }, [termToSearch, textAbbreviationDocuments]);
}
